import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useMessageQueue from '../../../hooks/useMessageQueue';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import MessageQueueOverview from './MessageQueueOverview';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import Page from '../../../components/Page/Page';
import MessageQueueBehavior from './MessageQueueBehavior';
import FunctionsTable from '../../../components/FunctionsTable/FunctionsTable';
import { GetFunctionsQuery } from '@hud/portal-dtos/webapp-function.dto';
import { useDebouncedValue } from '@mantine/hooks';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import useFlowFunctions from '../../../hooks/useFlowFunctions';
import { useFunctionFilters } from '../../../hooks/useFunctionFilter';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import useAnalytics from '../../../hooks/useAnalytics';
import { FunctionBehaviorChartContainer, Row } from './MessageQueuePage.styles';
import { Row as RowBase } from '@hud/web-components/Layout/Layout';
import ErrorsTable from '../../../components/ErrorsTable/ErrorsTable';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import { ThresholdFilter } from '@hud/portal-dtos/webapp-common.dto';
import {
  CommonThresholdQueryParams,
  getThresholdFilter,
  ThresholdFilterKey,
} from '../../../utils';
import { Events } from '../../../analytics';

type MessageQueuePageQueryParams = CommonTableQueryParams &
  CommonThresholdQueryParams & {
    files: string[];
    flowDeployments: string[];
    showUnnamed: boolean;
  };

export default function MessageQueuePage() {
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const { flowId } = useParams<{ flowId: string }>();
  const analytics = useAnalytics();
  const [initialLoad, setInitialLoad] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const functionFilters = useFunctionFilters();
  const location = useLocation();

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<MessageQueuePageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'durationAvg.value',
      desc: true,

      files: [],
      flowDeployments: [],
      showUnnamed: false,
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  const defaultBreadcrumbs = [
    { title: 'MessageQueues', to: '/explore/messageQueues', nowrap: true },
  ];

  const serviceId = searchParams.get('service');
  if (!serviceId) {
    throw new Error('Service ID is not provided');
  }

  const messageQueueQuery = useMessageQueue(parseInt(flowId as string), {
    timeFrame,
    environmentName: environment?.name,
    serviceId: parseInt(serviceId),
  });

  const [functionsPages, setFunctionsPages] = useState<number>(0);

  const clearAllFilters = () => {
    setPageState({
      files: [],
      flowDeployments: [],
      showUnnamed: false,
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });
  };

  const handleFilteredFilePathsChange = (value: string[]) => {
    setPageState({ files: value, page: 1 });
  };

  const handleFilteredFlowDeploymentsChange = useCallback(
    (value: string[]) => {
      setPageState({ flowDeployments: value, page: 1 });
    },
    [setPageState],
  );

  const handleShowUnnamedChange = (showUnnamed: boolean) => {
    setPageState({ showUnnamed, page: 1 });
  };

  const handleThresholdFilterChange = (
    key: ThresholdFilterKey,
    value: ThresholdFilter | undefined,
    eventName: keyof Events,
  ) => {
    setPageState({
      [key]: value?.value,
      [`${key}_op`]: value?.operator,
      page: 1,
    });

    analytics.capture(eventName, {
      environment: environment?.name,
      flow_id: Number.parseInt(flowId as string),
      toggle_value: !!value,
      filter_value: value?.value ?? 0,
      filter_operator: value?.operator ?? 'gte',
    });
  };

  const handleFilteredDurationAvgChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'durationAvg',
      value,
      'message_queue_functions_duration_avg_filter_toggled',
    );
  };

  const handleFilteredNormalizedRunsChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'normalizedRuns',
      value,
      'message_queue_functions_normalized_runs_filter_toggled',
    );
  };

  const handleFilteredErrorRateChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'errorRate',
      value,
      'message_queue_functions_error_rate_filter_toggled',
    );
  };

  const functionsQueryParams = useMemo<GetFunctionsQuery>(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetFunctionsQuery['orderBy'],
      includeGhost: true,
      includeUnnamed: pageState.showUnnamed,
      timeFrame,
      filePaths: pageState.files,
      environmentName: environment?.name,
      flowDeployments: pageState.flowDeployments,
      durationAvg: getThresholdFilter(pageState.durationAvg, pageState.durationAvg_op),
      normalizedRuns: getThresholdFilter(
        pageState.normalizedRuns,
        pageState.normalizedRuns_op,
      ),
      errorRate: getThresholdFilter(pageState.errorRate, pageState.errorRate_op),
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      pageState.showUnnamed,
      pageState.files,
      timeFrame,
      environment?.name,
      pageState.flowDeployments,
      pageState.durationAvg,
      pageState.durationAvg_op,
      pageState.normalizedRuns,
      pageState.normalizedRuns_op,
      pageState.errorRate,
      pageState.errorRate_op,
    ],
  );
  const [debouncedFunctionsQueryParams] = useDebouncedValue(
    functionsQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );
  const functionsQuery = useFlowFunctions(
    parseInt(flowId as string),
    parseInt(serviceId),
    debouncedFunctionsQueryParams,
  );

  useEffect(() => {
    if (functionsQuery.isSuccess) {
      setFunctionsPages(Math.ceil(functionsQuery.data.countFiltered / DEFAULT_PAGE_SIZE));
    }
  }, [functionsQuery.isSuccess, functionsQuery.data]);

  useEffect(() => {
    if (messageQueueQuery.isSuccess) {
      document.title = `${messageQueueQuery.data.messageQueue.queueName} • ${messageQueueQuery.data.messageQueue.groupId} • Hud`;
    } else {
      document.title = 'Hud';
    }
  }, [
    messageQueueQuery.isSuccess,
    messageQueueQuery.data?.messageQueue.queueName,
    messageQueueQuery.data?.messageQueue.groupId,
  ]);

  // TODO: Why isn't it just a `useEffect()` with empty dependency??
  useEffect(() => {
    if (messageQueueQuery.isSuccess && !initialLoad) {
      analytics.capture('message_queue_viewed', {
        queue_name: messageQueueQuery.data.messageQueue.queueName,
        kafka_topic_group_id: messageQueueQuery.data.messageQueue.groupId ?? '',
        environment: environment?.name,
      });
      setInitialLoad(true);
    }
  }, [analytics, environment?.name, initialLoad, messageQueueQuery]);

  useEffect(() => {
    if (messageQueueQuery.isSuccess && functionsQuery.isSuccess) {
      analytics.capture('message_queue_functions_paginated', {
        queue_name: messageQueueQuery.data.messageQueue.queueName,
        kafka_topic_group_id: messageQueueQuery.data.messageQueue.groupId ?? '',
        page: tableState.page,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        search: tableState.search,
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.page]);

  useEffect(() => {
    if (messageQueueQuery.isSuccess && functionsQuery.isSuccess) {
      analytics.capture('message_queue_functions_searched', {
        queue_name: messageQueueQuery.data.messageQueue.queueName,
        kafka_topic_group_id: messageQueueQuery.data.messageQueue.groupId ?? '',
        search: tableState.search,
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.search]);

  useEffect(() => {
    if (messageQueueQuery.isSuccess && functionsQuery.isSuccess) {
      analytics.capture('message_queue_functions_sorted', {
        queue_name: messageQueueQuery.data.messageQueue.queueName,
        kafka_topic_group_id: messageQueueQuery.data.messageQueue.groupId ?? '',
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.orderBy.key, tableState.orderBy.sortOrder]);

  useEffect(() => {
    if (messageQueueQuery.isSuccess) {
      analytics.capture('message_queue_functions_hide_unnamed_functions_toggled', {
        queue_name: messageQueueQuery.data.messageQueue.queueName,
        kafka_topic_group_id: messageQueueQuery.data.messageQueue.groupId ?? '',
        toggle_value: pageState.showUnnamed,
        environment: environment?.name,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.showUnnamed]);

  const functionTableReferralBreadcrumbs = useMemo(() => {
    if (!messageQueueQuery.data) return;
    return [
      {
        title: 'Message Queue Functions',
        to: `/explore/messageQueues/${flowId}?${functionFilters}`,
        nowrap: true,
      },
    ] as BreadcrumbItem[];
  }, [functionFilters, messageQueueQuery.data, flowId]);

  const breadcrumbsFromState: BreadcrumbItem[] | null =
    location.state?.referralBreadcrumbs;
  const backToTopicsBreadcrumbs = breadcrumbsFromState || defaultBreadcrumbs;

  function getTitle() {
    const pageTitles: BreadcrumbItem[] = [];
    if (messageQueueQuery.isSuccess) {
      pageTitles.push({
        title: `${messageQueueQuery.data.messageQueue.queueName}${
          messageQueueQuery.data.isMultiGroup
            ? `(group ${messageQueueQuery.data.messageQueue.groupId})`
            : ''
        }`,
      });
    }
    return pageTitles;
  }

  if (messageQueueQuery.isError) {
    return (
      <Page
        header={backToTopicsBreadcrumbs}
        actions={
          <ActionsContainer>
            <EnvironmentMenu />
            <TimeFrameMenu />
            <ThemeButton />
          </ActionsContainer>
        }
      >
        {messageQueueQuery.error.response?.status === 404
          ? messageQueueQuery.error.response?.data.message
          : 'Something went wrong'}
      </Page>
    );
  }
  return (
    <Page
      header={
        messageQueueQuery.isLoading
          ? 'loading'
          : [...backToTopicsBreadcrumbs, ...getTitle()]
      }
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <Row>
        <MessageQueueOverview
          queue={messageQueueQuery.data?.messageQueue}
          loading={messageQueueQuery.isLoading}
          referralBreadcrumbs={functionTableReferralBreadcrumbs}
        />
      </Row>
      <RowBase>
        <FunctionBehaviorChartContainer
          title="Behavior"
          variant="filled"
          icon="webviewMenu.flow"
        >
          <MessageQueueBehavior
            queue={messageQueueQuery.data?.messageQueue}
            graph={messageQueueQuery.data?.graphs}
            deployments={messageQueueQuery.data?.deployments}
            loading={messageQueueQuery.isLoading}
            timeFrame={timeFrame}
            firstSeenAt={messageQueueQuery.data?.messageQueue.firstSeenAt}
          />
        </FunctionBehaviorChartContainer>
      </RowBase>
      <ErrorsTable
        errors={messageQueueQuery.data?.errors}
        function={
          messageQueueQuery.data
            ? {
                id: messageQueueQuery.data.messageQueue.id,
                name: messageQueueQuery.data.messageQueue.queueName,
                firstSeenAt: messageQueueQuery.data.messageQueue.firstSeenAt,
              }
            : undefined
        }
        isKafkaTopic={true}
        loading={messageQueueQuery.isLoading}
      />
      <FunctionsTable
        timeFrame={timeFrame}
        title={'Functions in this flow'}
        aggregatedEndpoint={Number.parseInt(flowId as string)}
        aggregatedService={parseInt(serviceId)}
        functions={functionsQuery.data?.functions ?? []}
        loading={functionsQuery.isLoading}
        total={functionsQuery.data?.total}
        filtered={functionsQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={functionsPages}
        currentPage={functionsPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        showUnnamed={pageState.showUnnamed}
        onShowUnnamedChange={handleShowUnnamedChange}
        filteredFilePaths={pageState.files}
        onFilteredFilePathsChange={handleFilteredFilePathsChange}
        referralBreadcrumbs={functionTableReferralBreadcrumbs}
        filteredDurationAvg={getThresholdFilter(
          pageState.durationAvg,
          pageState.durationAvg_op,
        )}
        onFilteredDurationAvgChange={handleFilteredDurationAvgChange}
        filteredNormalizedRuns={getThresholdFilter(
          pageState.normalizedRuns,
          pageState.normalizedRuns_op,
        )}
        onFilteredNormalizedRunsChange={handleFilteredNormalizedRunsChange}
        filteredErrorRate={getThresholdFilter(
          pageState.errorRate,
          pageState.errorRate_op,
        )}
        onFilteredErrorRateChange={handleFilteredErrorRateChange}
        flowDeploymentsInTimeframe={messageQueueQuery.data?.deployments}
        filteredFlowDeployments={pageState.flowDeployments}
        onFilteredFlowDeploymentsChange={handleFilteredFlowDeploymentsChange}
        onClearAllFilters={clearAllFilters}
      />
    </Page>
  );
}
